import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { DealDetail, DealEvent, NegotiationFieldMeta, ServicingType } from 'api'
import { Locale, Translation } from 'locale'
import {
  createActionsRenderer,
  createGroupedActionRenderer,
  dateFilterParams,
  gridOptionsBase,
  groupingRowColDef,
  StandardGridColumnId,
  StatusCellRenderer,
  TypedContext,
  TypedHeaderValueGetterParams,
  TypedValueGetterParams,
  ValidationCellRenderer,
  withAllContext
} from 'view/components/datagrid'
import {
  canEditMidTradeEventDetailEffectiveDate,
  canEditMidTradeEventDetailNotional,
  canEditMidTradeEventDetailStartConsideration,
  canEditMidTradeEventDetailStartConsiderationOverride,
  dateQuickFilterTextGetter,
  getFeeFormatOptions,
  getHaircutFormatOptions,
  getNotionalFormatOptions,
  getOwnDirection,
  getRateFormatOptions,
  getStartConsiderationFormatOptions,
  haircutTypeFormatter,
  makeBooleanFormatter,
  makeCanEditMidTradeEventDetailField,
  makeCusipGetter,
  makeDateFormatter,
  makeFeeFormatter,
  makeHaircutFilterGetter,
  makeHaircutFormatter,
  makeHaircutQuickFilterTextGetter,
  makeIsinGetter,
  makeNotionalFilterGetter,
  makeNotionalFormatter,
  makeNumberFilterGetter,
  makeNumberFormatter,
  makeNumberQuickFilterTextGetter,
  makeRateFilterGetter,
  makeRateFormatter,
  makeRateQuickFilterTextGetter,
  makeTimeFormatter,
  makeTradingEntityNameGetter,
  newNotionalHeaderValueGetter,
  notionalHeaderValueGetter,
  orderTermFormatter,
  orderTermQuickFilterTextGetter,
  servicingTypeFormatter,
  timeQuickFilterTextGetter
} from 'view/shared/datagrid'
import { MidTradeEventFieldNames, MidTradeEventsGridColumnId } from './MidTradeEventsColumnNames'
import { getNegotiatedValue } from 'view/routes/deals/components/utils'
import { getMidTradeEventStatusColor, getMidTradeEventStatusText } from '../shared/utils'
import {
  getNotionalModificationType,
  getRateDelta,
  getRateModificationType,
  getStringModificationType
} from 'view/routes/requests/shared/deltaGetters'
import { DeltaGroupHeaderComponent } from 'view/routes/requests/LiveNegotiationsGrid/DeltaGroupHeaderComponent'
import { headerTemplateDelta } from 'view/routes/requests/LiveNegotiationsGrid/headerTemplates'
import { NegotiableCellRenderer } from './NegotiableCellRenderer'
import { MidTradeEventActionsCell } from './MidTradeEventActionsCell'
import { BooleanSelectbox, DecisionMakerSelectbox, TradingDatePicker } from 'view/shared/entity'
import { NumericInput } from 'view/components/inputs'
import { HaircutTypeSelectbox } from 'view/shared/entity/haircutOptions/HaircutTypeSelectbox'
import { DecisionMakerHelper, getDateEditorFormat, MarketHelper } from 'model'
import { bondPriceMaxFieldLength, bondPriceMaxFractionDigits } from 'view/styles/constants'
import { ServicingSecondaryCollateralCombobox } from 'view/shared/entity/SecondaryCollateralSelectbox'
import { BASE_ASSET_TYPE, BASE_DIRECTION, BASE_ISSUE, BASE_MARKET_TYPE, BASE_ROE, BASE_ROS } from 'view/shared/datagrid/baseColumns'

const CHECKBOX: ColDef = {
  colId: StandardGridColumnId.CHECKBOX,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  headerCheckboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
  checkboxSelection: true,
  width: 36,
  maxWidth: 36,
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockPosition: true,
  lockVisible: true,
  filter: false,
  resizable: false,
  sortable: false
}

const GROUPING: ColDef = groupingRowColDef

const ACTIONS: ColDef = {
  colId: StandardGridColumnId.ACTIONS,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  equals: () => false,
  width: 220,
  minWidth: 220,
  headerComponent: withAllContext(createGroupedActionRenderer(MidTradeEventActionsCell)),
  cellClass: 'action-cell',
  cellRenderer: withAllContext(createActionsRenderer(MidTradeEventActionsCell)),
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockPosition: true,
  lockVisible: true,
  filter: false,
  sortable: false
}

const STATUS: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_STATUS,
  headerName: Locale.translate(Translation.TC_STATUS),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data && getMidTradeEventStatusText(params.data),
  equals: () => false,
  width: 80,
  minWidth: 80,
  filter: 'agSetColumnFilter',
  cellRenderer: StatusCellRenderer,
  cellRendererParams: {
    getStatusColor: getMidTradeEventStatusColor
  },
  lockVisible: true
}

const EVENT_TYPE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_EVENT_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
    const dealDetail = params.data
    if (dealDetail?.deal.servicingDetails) {
      return dealDetail.deal.servicingDetails.servicingType.servicingType
    }
    return dealDetail?.deal.lastEvent === DealEvent.SERVICING_ACCEPT
      ? dealDetail.deal.committedServicingType
      : dealDetail?.deal.rejectedOrWithdrawnServicingType
  },
  headerName: Locale.translate(Translation.TC_EVENT_TYPE),
  width: 100,
  filter: 'agSetColumnFilter',
  valueFormatter: servicingTypeFormatter
}

const COLLATERAL_ISIN_OR_BUI: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_COLLATERAL_ISIN_OR_BUI,
  valueGetter: makeIsinGetter(),
  headerName: Locale.translate(Translation.TC_ISIN_BUI),
  width: 140,
  filter: 'agSetColumnFilter'
}

const NEW_ISIN: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_ISIN,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.servicingSideDetailsMeta?.COLLATERAL?.isin,
  headerName: Locale.translate(Translation.TC_NEW_ISIN_BUI),
  equals: () => false,
  width: 140,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      dealDetail.servicingSideDetailsMeta?.COLLATERAL?.isin
        ? getStringModificationType(
            dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.COLLATERAL]?.negotiableFieldRequired
          )
        : null
  }
}

const SUBMITTING_USER: ColDef = {
  colId: MidTradeEventsGridColumnId.SUBMITTING_USER,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal?.orderSubmitUserId,
  headerName: Locale.translate(Translation.TC_SUBMITTING_USER),
  width: 250,
  filter: 'agSetColumnFilter',
  hide: true
}

const COLLATERAL_CUSIP: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_COLLATERAL_CUSIP,
  valueGetter: makeCusipGetter(),
  headerName: Locale.translate(Translation.TC_CUSIP),
  width: 140,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEW_COLLATERAL_CUSIP: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_COLLATERAL_CUSIP,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.servicingSideDetailsMeta?.COLLATERAL?.cusip,
  headerName: Locale.translate(Translation.TC_NEW_CUSIP),
  equals: () => false,
  width: 140,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      dealDetail.servicingSideDetailsMeta?.COLLATERAL?.cusip
        ? getStringModificationType(
            dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.COLLATERAL]?.negotiableFieldRequired
          )
        : null
  },
  hide: true
}

const COLLATERAL_NAME: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_COLLATERAL_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.collateral?.name ?? params.data?.collateralBasket?.name,
  headerName: Locale.translate(Translation.TC_COLLATERAL),
  width: 120,
  filter: 'agSetColumnFilter'
}

const NEW_COLLATERAL_NAME: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_COLLATERAL_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.servicingSideDetailsMeta?.COLLATERAL?.name,
  headerName: Locale.translate(Translation.TC_NEW_COLLATERAL),
  equals: () => false,
  width: 120,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      getStringModificationType(
        dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.COLLATERAL]?.negotiableFieldRequired
      )
  }
}

const SECONDARY_COLLATERAL_NAME: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_SECONDARY_COLLATERAL_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.secondaryInstrumentName,
  headerName: Locale.translate(Translation.TC_SECONDARY_COLLATERAL),
  width: 120,
  filter: 'agSetColumnFilter'
}

const NEW_SECONDARY_COLLATERAL_NAME: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_SECONDARY_COLLATERAL_NAME,
  headerName: Locale.translate(Translation.TC_NEW_SECONDARY_COLLATERAL),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.negotiatedSecondaryInstrumentName,
  equals: () => false,
  width: 120,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      getStringModificationType(
        dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.SECONDARY_BASKET_ID]?.negotiableFieldRequired
      )
  },
  editable: makeCanEditMidTradeEventDetailField(NegotiationFieldMeta.SECONDARY_BASKET_ID),
  cellEditorParams: {
    inputComponent: ServicingSecondaryCollateralCombobox,
    inputParams: (dealDetail: DealDetail) => ({
      detail: dealDetail
    }),
    defaultField: MidTradeEventFieldNames.SECONDARY_BASKET_ID_NEGOTIATED,
    updateField: MidTradeEventFieldNames.SECONDARY_BASKET_ID_MODIFIED,
    isModifiedField: MidTradeEventFieldNames.SECONDARY_BASKET_ID_IS_MODIFIED,
    validationField: MidTradeEventFieldNames.SECONDARY_BASKET_ID
  }
}

const STOCK_BORROWING_LENDING_TYPE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_STOCK_BORROWING_LENDING_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.stockBorrowingLendingType,
  headerName: Locale.translate(Translation.TC_STOCK_BORROW_LENDING_TYPE),
  width: 120,
  filter: 'agSetColumnFilter'
}

const NEW_STOCK_BORROWING_LENDING_TYPE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_STOCK_BORROWING_LENDING_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(
      params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.STOCK_BORROWING_LENDING_TYPE]?.negotiableFieldRequired
    ),
  headerName: Locale.translate(Translation.TC_NEW_STOCK_BORROW_LENDING_TYPE),
  equals: () => false,
  width: 120,
  filter: 'agDateColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      getStringModificationType(
        dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.STOCK_BORROWING_LENDING_TYPE]?.negotiableFieldRequired
      )
  }
}

const DIRECTION: ColDef = {
  ...BASE_DIRECTION,
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_DIRECTION,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    params.data && getOwnDirection(params.data.deal.direction, params.data.ownOrder),
  width: 100
}

const NOTIONAL: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NOTIONAL,
  headerValueGetter: notionalHeaderValueGetter,
  headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.notional,
  width: 95,
  minWidth: 95,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNotionalFormatter(),
  filterValueGetter: makeNotionalFilterGetter()
}

const NOTIONAL_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL,
      headerValueGetter: newNotionalHeaderValueGetter,
      headerName: Locale.translate(Translation.TC_NEW_NOTIONAL_SHORT),
      valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
        getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.NOTIONAL]?.negotiableFieldRequired),
      equals: () => false,
      width: 110,
      minWidth: 110,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell',
      headerComponent: DeltaGroupHeaderComponent,
      headerComponentParams: {
        children: [
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL_ORIGINAL,
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL_DELTA
        ]
      },
      valueFormatter: makeNotionalFormatter(),
      filterValueGetter: makeNotionalFilterGetter(),
      cellRenderer: NegotiableCellRenderer,
      cellRendererParams: {
        getModificationType: (dealDetail: DealDetail) =>
          getNotionalModificationType(
            dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.NOTIONAL]?.negotiableFieldRequired
          ),
        validationField: MidTradeEventFieldNames.NOTIONAL
      },
      editable: canEditMidTradeEventDetailNotional,
      cellEditorParams: {
        inputComponent: NumericInput,
        inputParams: (dealDetail: DealDetail, context: any) => ({
          min: dealDetail.collateral?.ticketIncrement,
          step: dealDetail.collateral?.ticketIncrement,
          formatOptions: getNotionalFormatOptions(context.userPreference.amountFormat)
        }),
        defaultField: MidTradeEventFieldNames.NOTIONAL_NEGOTIATED,
        updateField: MidTradeEventFieldNames.NOTIONAL_MODIFIED,
        isModifiedField: MidTradeEventFieldNames.NOTIONAL_IS_MODIFIED,
        validationField: MidTradeEventFieldNames.NOTIONAL
      },
      lockPinned: true,
      enableRowGroup: false,
      cellClassRules: {
        'paren-on-negative-cell': (params: any) => params.node.group && params.value > 0
      }
    },
    {
      colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL_ORIGINAL,
      valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
        params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.NOTIONAL]?.negotiableFieldRequired?.original,
      headerName: Locale.translate(Translation.TC_ORIGINAL_SHORT),
      headerValueGetter: (params: TypedHeaderValueGetterParams<DealDetail>) =>
        Locale.translate(Translation.TC_ORIGINAL, { amountFormat: params.context.userPreference.amountFormat }),
      width: 115,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        parent: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL
      },
      valueFormatter: makeNotionalFormatter(),
      filterValueGetter: makeNotionalFilterGetter(),
      enableRowGroup: false,
      lockPinned: true,
      hide: true // delta groups are initially hidden
    },
    {
      colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL_DELTA,
      valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
        getRateDelta(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.RATE]?.negotiableFieldRequired),
      headerValueGetter: newNotionalHeaderValueGetter,
      headerName: Locale.translate(Translation.TC_NEW_NOTIONAL_SHORT),
      width: 110,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        template: headerTemplateDelta,
        parent: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL,
        delta: true
      },
      valueFormatter: makeNotionalFormatter(true),
      filterValueGetter: makeNotionalFilterGetter(true),
      getQuickFilterText: makeNumberQuickFilterTextGetter({ sign: true }),
      enableRowGroup: false,
      lockPinned: true,
      hide: true // delta groups are initially hidden
    }
  ]
}

const COUNTERPARTY_NAME = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_COUNTERPARTY_NAME,
  valueGetter: makeTradingEntityNameGetter(false),
  headerName: Locale.translate(Translation.TC_COUNTERPARTY),
  width: 150,
  filter: 'agSetColumnFilter'
}

const START_DATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_START_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.startDate,
  headerName: Locale.translate(Translation.TC_START_DATE),
  width: 120,
  ...dateFilterParams,
  filter: 'agDateColumnFilter',
  getQuickFilterText: dateQuickFilterTextGetter,
  valueFormatter: makeDateFormatter(),
  hide: true
}

const NEW_START_DATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_START_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.START_DATE]?.negotiableFieldRequired),
  headerName: Locale.translate(Translation.TC_NEW_START_DATE),
  equals: () => false,
  width: 120,
  ...dateFilterParams,
  filter: 'agDateColumnFilter',
  getQuickFilterText: dateQuickFilterTextGetter,
  valueFormatter: makeDateFormatter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      dealDetail.servicingSideDetailsMeta?.START_DATE
        ? getStringModificationType(
            dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.START_DATE]?.negotiableFieldRequired
          )
        : null
  },
  hide: true
}

const END_DATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_END_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.endDate,
  headerName: Locale.translate(Translation.TC_END_DATE),
  width: 120,
  ...dateFilterParams,
  filter: 'agDateColumnFilter',
  getQuickFilterText: dateQuickFilterTextGetter,
  valueFormatter: makeDateFormatter(),
  hide: true
}

const NEW_END_DATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_END_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.END_DATE]?.negotiableFieldRequired),
  headerName: Locale.translate(Translation.TC_NEW_END_DATE),
  equals: () => false,
  width: 120,
  ...dateFilterParams,
  filter: 'agDateColumnFilter',
  getQuickFilterText: dateQuickFilterTextGetter,
  valueFormatter: makeDateFormatter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      dealDetail.servicingSideDetailsMeta?.END_DATE
        ? getStringModificationType(
            dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.END_DATE]?.negotiableFieldRequired
          )
        : null
  },
  hide: true
}

const DEAL_TERM: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_TERM,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.term,
  headerName: Locale.translate(Translation.TC_TERM),
  width: 120,
  filter: 'agSetColumnFilter',
  valueFormatter: orderTermFormatter,
  getQuickFilterText: orderTermQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const NEW_DEAL_TERM: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_TERM,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.TERM]?.negotiableFieldRequired),
  headerName: Locale.translate(Translation.TC_NEW_TERM),
  equals: () => false,
  width: 120,
  filter: 'agSetColumnFilter',
  valueFormatter: orderTermFormatter,
  getQuickFilterText: orderTermQuickFilterTextGetter,
  enableRowGroup: false,
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      dealDetail.servicingSideDetailsMeta?.TERM
        ? getStringModificationType(dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.TERM]?.negotiableFieldRequired)
        : null
  },
  hide: true
}

const RATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_RATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.rate,
  headerName: Locale.translate(Translation.TC_RATE),
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeRateFormatter(),
  filterValueGetter: makeRateFilterGetter(),
  getQuickFilterText: makeRateQuickFilterTextGetter()
}

const RATE_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_RATE,
      valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
        if (!params.data) {
          return
        }
        const { data } = params
        return getNegotiatedValue(data.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.RATE]?.negotiableFieldRequired)
      },
      headerName: Locale.translate(Translation.TC_NEW_RATE),
      equals: () => false,
      width: 88,
      minWidth: 88,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell',
      valueFormatter: makeRateFormatter(false),
      filterValueGetter: makeRateFilterGetter(),
      getQuickFilterText: makeRateQuickFilterTextGetter(),
      cellRenderer: NegotiableCellRenderer,
      cellRendererParams: {
        getModificationType: (dealDetail: DealDetail) =>
          getRateModificationType(dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.RATE]?.negotiableFieldRequired),
        isRateField: true,
        validationField: MidTradeEventFieldNames.RATE
      },
      editable: makeCanEditMidTradeEventDetailField(NegotiationFieldMeta.RATE),
      cellEditorParams: {
        inputComponent: NumericInput,
        inputParams: (dealDetail: DealDetail) => ({
          formatOptions: getRateFormatOptions(dealDetail.referenceRate)
        }),
        defaultField: MidTradeEventFieldNames.RATE_NEGOTIATED,
        updateField: MidTradeEventFieldNames.RATE_MODIFIED,
        isModifiedField: MidTradeEventFieldNames.RATE_IS_MODIFIED,
        validationField: MidTradeEventFieldNames.RATE
      },
      lockPinned: true,
      enableRowGroup: false
    }
  ]
}

const FEE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_FEE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.fee,
  headerName: Locale.translate(Translation.TC_FEE),
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeFeeFormatter(),
  filterValueGetter: makeNumberFilterGetter(),
  getQuickFilterText: makeNumberQuickFilterTextGetter()
}

const NEW_FEE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_FEE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
    if (!params.data) {
      return
    }
    const { data } = params
    return getNegotiatedValue(data.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.FEE]?.negotiableFieldRequired)
  },
  headerName: Locale.translate(Translation.TC_NEW_FEE),
  equals: () => false,
  width: 88,
  minWidth: 88,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeFeeFormatter(),
  filterValueGetter: makeNumberFilterGetter(),
  getQuickFilterText: makeNumberQuickFilterTextGetter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      getRateModificationType(dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.FEE]?.negotiableFieldRequired),
    isRateField: true,
    validationField: MidTradeEventFieldNames.FEE
  },
  editable: makeCanEditMidTradeEventDetailField(NegotiationFieldMeta.FEE),
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: () => ({
      formatOptions: getFeeFormatOptions()
    }),
    defaultField: MidTradeEventFieldNames.FEE_NEGOTIATED,
    updateField: MidTradeEventFieldNames.FEE_MODIFIED,
    isModifiedField: MidTradeEventFieldNames.FEE_IS_MODIFIED,
    validationField: MidTradeEventFieldNames.FEE
  },
  lockPinned: true,
  enableRowGroup: false
}

const HAIRCUT: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_HAIRCUT,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.haircut,
  headerName: Locale.translate(Translation.TC_HAIRCUT),
  equals: () => false,
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeHaircutFormatter(),
  filterValueGetter: makeHaircutFilterGetter(),
  getQuickFilterText: makeHaircutQuickFilterTextGetter(),
  enableRowGroup: false,
  hide: true
}

const HAIRCUT_GROUP: ColGroupDef = {
  marryChildren: true,
  children: [
    {
      colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_HAIRCUT,
      valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
        if (!params.data) {
          return
        }
        const { data } = params
        return getNegotiatedValue(data.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.HAIRCUT]?.negotiableFieldRequired)
      },
      headerName: Locale.translate(Translation.TC_NEW_HAIRCUT),
      equals: () => false,
      width: 102,
      minWidth: 102,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell',
      headerComponent: DeltaGroupHeaderComponent,
      headerComponentParams: {
        children: [
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_HAIRCUT_TARGET,
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_HAIRCUT_DELTA
        ]
      },
      valueFormatter: makeHaircutFormatter(),
      filterValueGetter: makeHaircutFilterGetter(),
      getQuickFilterText: makeHaircutQuickFilterTextGetter(),
      cellRenderer: NegotiableCellRenderer,
      cellRendererParams: {
        getModificationType: (dealDetail: DealDetail) =>
          getRateModificationType(
            dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.HAIRCUT]?.negotiableFieldRequired
          ),
        validationField: MidTradeEventFieldNames.HAIRCUT,
        dataTestId: 'MidTradeEventsGridModel-HaircutNegotiableCellRenderer'
      },
      editable: makeCanEditMidTradeEventDetailField(NegotiationFieldMeta.HAIRCUT),
      cellEditorParams: {
        inputComponent: NumericInput,
        inputParams: (dealDetail: DealDetail, context: TypedContext<DealDetail>) => ({
          formatOptions: getHaircutFormatOptions(undefined, context.userPreference.haircutSign, dealDetail.ownOrder, dealDetail.direction),
          dataTestId: 'MidTradeEventsGridModel-HaircutNumericInput'
        }),
        defaultField: MidTradeEventFieldNames.HAIRCUT_NEGOTIATED,
        updateField: MidTradeEventFieldNames.HAIRCUT_MODIFIED,
        isModifiedField: MidTradeEventFieldNames.HAIRCUT_IS_MODIFIED,
        validationField: MidTradeEventFieldNames.HAIRCUT
      },
      lockPinned: true,
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    },
    {
      colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_HAIRCUT_TARGET,
      valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
        if (!params.data) {
          return
        }
        const { data } = params
        return data.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.HAIRCUT]?.negotiableFieldRequired?.original
      },
      headerName: Locale.translate(Translation.TC_TARGET),
      equals: () => false,
      width: 80,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        parent: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_HAIRCUT
      },
      valueFormatter: makeHaircutFormatter(),
      filterValueGetter: makeHaircutFilterGetter(),
      getQuickFilterText: makeHaircutQuickFilterTextGetter(),
      lockPinned: true,
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    },
    {
      colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_HAIRCUT_DELTA,
      valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
        if (!params.data) {
          return
        }
        const { data } = params
        return getRateDelta(data.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.HAIRCUT]?.negotiableFieldRequired)
      },
      headerName: Locale.translate(Translation.TC_NEW_HAIRCUT),
      equals: () => false,
      width: 80,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      headerClass: 'group-header-cell group-header-cell-right-align',
      headerComponentParams: {
        template: headerTemplateDelta,
        parent: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_HAIRCUT,
        delta: true
      },
      valueFormatter: makeHaircutFormatter(true, true),
      filterValueGetter: makeHaircutFilterGetter(true, true),
      getQuickFilterText: makeHaircutQuickFilterTextGetter(true),
      lockPinned: true,
      enableRowGroup: false,
      hide: true // delta groups are initially hidden
    }
  ]
}

const HAIRCUT_TYPE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_HAIRCUT_TYPE,
  headerName: Locale.translate(Translation.TC_HAIRCUT_TYPE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.haircutType,
  valueFormatter: haircutTypeFormatter,
  equals: () => false,
  width: 120,
  filter: 'agSetColumnFilter',
  hide: true
}

const NEW_HAIRCUT_TYPE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_HAIRCUT_TYPE,
  headerName: Locale.translate(Translation.TC_NEW_HAIRCUT_TYPE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.HAIRCUT_TYPE]?.negotiableFieldRequired),
  valueFormatter: haircutTypeFormatter,
  equals: () => false,
  width: 150,
  filter: 'agSetColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      getStringModificationType(
        dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.HAIRCUT_TYPE]?.negotiableFieldRequired
      ),
    validationField: MidTradeEventFieldNames.HAIRCUT_TYPE
  },
  editable: makeCanEditMidTradeEventDetailField(NegotiationFieldMeta.HAIRCUT_TYPE),
  cellEditorParams: {
    inputComponent: HaircutTypeSelectbox,
    defaultField: MidTradeEventFieldNames.HAIRCUT_TYPE_NEGOTIATED,
    updateField: MidTradeEventFieldNames.HAIRCUT_TYPE_MODIFIED,
    isModifiedField: MidTradeEventFieldNames.HAIRCUT_TYPE_IS_MODIFIED,
    validationField: MidTradeEventFieldNames.HAIRCUT_TYPE
  },
  lockPinned: true,
  enableRowGroup: false,
  hide: true
}

const REFERENCE_RATE_NAME: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_REFERENCE_RATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.referenceRate?.name,
  headerName: Locale.translate(Translation.TC_REFERENCE_RATE),
  width: 180,
  filter: 'agSetColumnFilter',
  hide: true
}

const FLOAT_CRYSTALLISATION_DAYS = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_FLOAT_CRYSTALLISATION_DAYS,
  headerName: Locale.translate(Translation.TC_FLOAT_CRYSTALLISATION_DAYS),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.floatCrystallisationDays,
  width: 120,
  filter: 'agSetColumnFilter',
  equals: () => false,
  editable: false,
  lockPinned: true
}

const BOND_PRICE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_BOND_PRICE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.bondPrice,
  headerName: Locale.translate(Translation.TC_COLLATERAL_PRICE),
  equals: () => false,
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter'
}

const NEW_BOND_PRICE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_BOND_PRICE,
  headerName: Locale.translate(Translation.TC_NEW_COLLATERAL_PRICE),
  valueFormatter: makeNumberFormatter({
    maxFractionDigits: bondPriceMaxFractionDigits
  }),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.BOND_PRICE]?.negotiableFieldRequired),
  equals: () => false,
  width: 120,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      getStringModificationType(
        dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.BOND_PRICE]?.negotiableFieldRequired
      ),
    validationField: MidTradeEventFieldNames.BOND_PRICE
  },
  editable: makeCanEditMidTradeEventDetailField(NegotiationFieldMeta.BOND_PRICE),
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: () => ({
      min: 0,
      maxLength: bondPriceMaxFieldLength,
      formatOptions: {
        maxFractionDigits: bondPriceMaxFractionDigits
      }
    }),
    defaultField: MidTradeEventFieldNames.BOND_PRICE_NEGOTIATED,
    updateField: MidTradeEventFieldNames.BOND_PRICE_MODIFIED,
    isModifiedField: MidTradeEventFieldNames.BOND_PRICE_IS_MODIFIED,
    validationField: MidTradeEventFieldNames.BOND_PRICE
  }
}

const getEffectiveField = (servicingType: ServicingType | undefined) => {
  switch (servicingType) {
    case ServicingType.TERMINATION:
    case ServicingType.RETURN_RECALL:
      return NegotiationFieldMeta.END_DATE
    case ServicingType.RERATE:
      return NegotiationFieldMeta.RERATE_DATE
    default:
      return NegotiationFieldMeta.START_DATE
  }
}

const getEffectiveDateValidationField = (dealDetail: DealDetail) => {
  if (!dealDetail) {
    return null
  }
  switch (dealDetail.deal.servicingDetails?.servicingType.servicingType) {
    case ServicingType.TERMINATION:
    case ServicingType.RETURN_RECALL:
      return MidTradeEventFieldNames.END_DATE
    case ServicingType.RERATE:
      return MidTradeEventFieldNames.RERATE_DATE
    default:
      return MidTradeEventFieldNames.START_DATE
  }
}

const getEffectiveDateDefaultField = (dealDetail: DealDetail) => {
  if (!dealDetail) {
    return null
  }
  switch (dealDetail.deal.servicingDetails?.servicingType.servicingType) {
    case ServicingType.TERMINATION:
    case ServicingType.RETURN_RECALL:
      return MidTradeEventFieldNames.END_DATE_NEGOTIATED
    case ServicingType.RERATE:
      return MidTradeEventFieldNames.RERATE_DATE_NEGOTIATED
    default:
      return MidTradeEventFieldNames.START_DATE_NEGOTIATED
  }
}

const getEffectiveDateUpdateField = (dealDetail: DealDetail) => {
  if (!dealDetail) {
    return null
  }
  switch (dealDetail.deal.servicingDetails?.servicingType.servicingType) {
    case ServicingType.TERMINATION:
    case ServicingType.RETURN_RECALL:
      return MidTradeEventFieldNames.END_DATE_MODIFIED
    case ServicingType.RERATE:
      return MidTradeEventFieldNames.RERATE_DATE_MODIFIED
    default:
      return MidTradeEventFieldNames.START_DATE_MODIFIED
  }
}

const getEffectiveDateIsModifiedField = (dealDetail: DealDetail) => {
  if (!dealDetail) {
    return null
  }
  switch (dealDetail.deal.servicingDetails?.servicingType.servicingType) {
    case ServicingType.TERMINATION:
    case ServicingType.RETURN_RECALL:
      return MidTradeEventFieldNames.END_DATE_IS_MODIFIED
    case ServicingType.RERATE:
      return MidTradeEventFieldNames.RERATE_DATE_IS_MODIFIED
    default:
      return MidTradeEventFieldNames.START_DATE_IS_MODIFIED
  }
}

const EFFECTIVE_DATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_EFFECTIVE_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
    const negotiationFieldMeta = getEffectiveField(params.data?.deal.servicingDetails?.servicingType.servicingType || undefined)

    return getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[negotiationFieldMeta]?.negotiableFieldRequired)
  },
  headerName: Locale.translate(Translation.TC_EFFECTIVE_DATE),
  equals: () => false,
  width: 120,
  ...dateFilterParams,
  filter: 'agDateColumnFilter',
  getQuickFilterText: dateQuickFilterTextGetter,
  valueFormatter: makeDateFormatter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: () => null,
    validationField: getEffectiveDateValidationField
  },
  editable: canEditMidTradeEventDetailEffectiveDate,
  cellEditorParams: {
    inputComponent: TradingDatePicker,
    inputParams: (dealDetail: DealDetail, context: any) => ({
      currency: dealDetail.collateral?.currency,
      dateFormat: getDateEditorFormat(context.userPreference.dateFormat)
    }),
    defaultField: getEffectiveDateDefaultField,
    updateField: getEffectiveDateUpdateField,
    isModifiedField: getEffectiveDateIsModifiedField,
    validationField: getEffectiveDateValidationField
  }
}

const START_CONSIDERATION: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_START_CONSIDERATION,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.startConsideration,
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  enableRowGroup: false
}

const NEW_START_CONSIDERATION: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_START_CONSIDERATION,
  headerName: Locale.translate(Translation.TC_NEW_START_CONSIDERATION),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(
      params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.START_CONSIDERATION]?.negotiableFieldRequired
    ),
  equals: () => false,
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      getStringModificationType(
        dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.START_CONSIDERATION]?.negotiableFieldRequired
      ),
    validationField: MidTradeEventFieldNames.START_CONSIDERATION
  },
  editable: canEditMidTradeEventDetailStartConsideration,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: (dealDetail: DealDetail) => ({
      min: dealDetail.collateral?.ticketIncrement,
      step: dealDetail.collateral?.ticketIncrement,
      formatOptions: getStartConsiderationFormatOptions()
    }),
    defaultField: MidTradeEventFieldNames.START_CONSIDERATION_NEGOTIATED,
    updateField: MidTradeEventFieldNames.START_CONSIDERATION_MODIFIED,
    isModifiedField: MidTradeEventFieldNames.START_CONSIDERATION_IS_MODIFIED,
    validationField: MidTradeEventFieldNames.START_CONSIDERATION
  },
  enableRowGroup: false
}

const START_CONSIDERATION_OVERRIDE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_START_CONSIDERATION_OVERRIDE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.startConsiderationOverride,
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE),
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeBooleanFormatter(),
  enableRowGroup: false
}

const NEW_START_CONSIDERATION_OVERRIDE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_START_CONSIDERATION_OVERRIDE,
  headerName: Locale.translate(Translation.TC_NEW_START_CONSIDERATION_OVERRIDE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(
      params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE]?.negotiableFieldRequired
    ),
  equals: () => false,
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeBooleanFormatter(),
  cellRenderer: NegotiableCellRenderer,
  cellRendererParams: {
    getModificationType: (dealDetail: DealDetail) =>
      getStringModificationType(
        dealDetail.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE]?.negotiableFieldRequired
      ),
    validationField: MidTradeEventFieldNames.START_CONSIDERATION_OVERRIDE
  },
  editable: canEditMidTradeEventDetailStartConsiderationOverride,
  cellEditorParams: {
    inputComponent: BooleanSelectbox,
    defaultField: MidTradeEventFieldNames.START_CONSIDERATION_OVERRIDE_NEGOTIATED,
    updateField: MidTradeEventFieldNames.START_CONSIDERATION_OVERRIDE_MODIFIED,
    isModifiedField: MidTradeEventFieldNames.START_CONSIDERATION_OVERRIDE_IS_MODIFIED,
    validationField: MidTradeEventFieldNames.START_CONSIDERATION_OVERRIDE
  },
  enableRowGroup: false
}

const END_CONSIDERATION: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_END_CONSIDERATION,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.endConsideration,
  headerName: Locale.translate(Translation.TC_END_CONSIDERATION),
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  enableRowGroup: false,
  hide: true
}

const NEW_END_CONSIDERATION: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_END_CONSIDERATION,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(
      params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.END_CONSIDERATION]?.negotiableFieldRequired
    ),
  headerName: Locale.translate(Translation.TC_NEW_END_CONSIDERATION),
  equals: () => false,
  width: 150,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  enableRowGroup: false,
  hide: true
}

const NEW_REPO_INTEREST: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_REPO_INTEREST,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.REPO_INTEREST]?.negotiableFieldRequired),
  headerName: Locale.translate(Translation.TC_NEW_REPO_INTEREST),
  equals: () => false,
  width: 100,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  hide: true
}

const NOTICE_PERIOD_DAYS: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NOTICE_PERIOD_DAYS,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.noticePeriodDays,
  headerName: Locale.translate(Translation.TC_NOTICE_PERIOD_DAYS),
  width: 80,
  type: 'numericColumn',
  filter: 'agNumberColumnFilter',
  valueFormatter: makeNumberFormatter(),
  hide: true
}

const MY_ENTITY: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_MY_ENTITY,
  valueGetter: makeTradingEntityNameGetter(true),
  headerName: Locale.translate(Translation.TC_MY_ENTITY),
  width: 150,
  filter: 'agSetColumnFilter'
}

const COLLATERAL_CURRENCY: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_COLLATERAL_CURRENCY,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.currency,
  headerName: Locale.translate(Translation.TC_CURRENCY),
  width: 90,
  filter: 'agSetColumnFilter',
  hide: true
}

const createUTI = (isAdmin: boolean): ColDef => ({
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_UTI,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    isAdmin && params.data?.ownOrder ? params.data?.deal.initiatorUtiRtn : params.data?.deal.responderUtiRtn,
  headerName: Locale.translate(Translation.TC_UTI),
  width: 100,
  filter: 'agSetColumnFilter',
  enableRowGroup: false,
  hide: true
})

const DEAL_ID: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_DEAL_ID,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.dealId,
  headerName: Locale.translate(Translation.TC_DEAL_ID),
  width: 100,
  filter: 'agSetColumnFilter'
}

const LAST_UPDATED_BY: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_LAST_UPDATED_BY,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.lastUpdatedBy,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
  width: 130,
  filter: 'agSetColumnFilter'
}

const TRADE_DATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_TRADE_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.tradedAt,
  headerName: Locale.translate(Translation.TC_DEAL_DATE),
  width: 150,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  hide: true
}

const TRADE_TIME: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_TRADE_TIME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.tradedAt,
  headerName: Locale.translate(Translation.TC_DEAL_TIME),
  width: 150,
  filter: false,
  valueFormatter: makeTimeFormatter(),
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const PRODUCT: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_PRODUCT,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.product?.name,
  headerName: Locale.translate(Translation.TC_PRODUCT),
  width: 80,
  filter: 'agSetColumnFilter',
  hide: true
}

const SECTOR: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_PRODUCT_SECTOR,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.productSector?.name,
  headerName: Locale.translate(Translation.TC_SECTOR),
  width: 100,
  filter: 'agSetColumnFilter',
  hide: true
}

const MARKET: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_MARKET,
  headerName: Locale.translate(Translation.TC_MARKET),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => {
    const market = params.data?.deal.market
    return market && MarketHelper.getName(market)
  },
  width: 80,
  filter: 'agSetColumnFilter',
  hide: true
}

const INVESTMENT_DECISION_MAKER = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_INVESTMENT_DECISION_MAKER,
  valueGetter: ({ data }: TypedValueGetterParams<DealDetail>) => {
    if (!data) {
      return
    }

    const idm = data.ownOrder
      ? data.servicingSideDetailsMeta?.INITIATOR_INVESTMENT_DECISION_MAKER
      : data.servicingSideDetailsMeta?.RESPONDER_INVESTMENT_DECISION_MAKER

    const historicalIdm = data.ownOrder ? data.initiatorInvestmentDecisionMaker : data.responderInvestmentDecisionMaker

    return idm ? DecisionMakerHelper.getName(idm) : historicalIdm ? DecisionMakerHelper.getName(historicalIdm) : null
  },
  headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER),
  equals: () => false,
  width: 180,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER
        : MidTradeEventFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER
  },
  editable: true,
  cellEditorParams: {
    inputComponent: DecisionMakerSelectbox,
    defaultField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_NEGOTIATED
        : MidTradeEventFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_NEGOTIATED,
    updateField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_MODIFIED
        : MidTradeEventFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_MODIFIED,
    isModifiedField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_IS_MODIFIED
        : MidTradeEventFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_IS_MODIFIED,
    validationField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER
        : MidTradeEventFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER
  },
  lockPinned: true,
  enableRowGroup: false,
  hide: true
}

const EXECUTION_DECISION_MAKER = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_EXECUTION_DECISION_MAKER,
  valueGetter: ({ data }: TypedValueGetterParams<DealDetail>) => {
    if (!data) {
      return
    }

    const edm = data.ownOrder
      ? data.servicingSideDetailsMeta?.INITIATOR_EXECUTION_DECISION_MAKER
      : data.servicingSideDetailsMeta?.RESPONDER_EXECUTION_DECISION_MAKER

    const historicalEdm = data.ownOrder ? data.initiatorExecutionDecisionMaker : data.responderExecutionDecisionMaker

    return edm ? DecisionMakerHelper.getName(edm) : historicalEdm ? DecisionMakerHelper.getName(historicalEdm) : null
  },
  headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER),
  equals: () => false,
  width: 180,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: {
    validationField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_EXECUTION_DECISION_MAKER
        : MidTradeEventFieldNames.RESPONDER_EXECUTION_DECISION_MAKER
  },
  editable: true,
  cellEditorParams: {
    inputComponent: DecisionMakerSelectbox,
    defaultField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_NEGOTIATED
        : MidTradeEventFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_NEGOTIATED,
    updateField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_MODIFIED
        : MidTradeEventFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_MODIFIED,
    isModifiedField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_IS_MODIFIED
        : MidTradeEventFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_IS_MODIFIED,
    validationField: (dealDetail: DealDetail) =>
      dealDetail.ownOrder
        ? MidTradeEventFieldNames.INITIATOR_EXECUTION_DECISION_MAKER
        : MidTradeEventFieldNames.RESPONDER_EXECUTION_DECISION_MAKER
  },
  lockPinned: true,
  enableRowGroup: false,
  hide: true
}

const DEAL_UPDATED_AT_DATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_LAST_UPDATED_DATE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.lastUpdatedAt,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_DATE),
  width: 130,
  filter: 'agDateColumnFilter',
  ...dateFilterParams,
  valueFormatter: makeDateFormatter(),
  getQuickFilterText: dateQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const DEAL_UPDATED_AT_TIME: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_LAST_UPDATED_TIME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.lastUpdatedAt,
  headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_TIME),
  width: 130,
  filter: false,
  valueFormatter: makeTimeFormatter(),
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const MY_ENTITY_SHORT_NAME: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_MY_ENTITY_SHORT_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.myEntityShortName,
  headerName: Locale.translate(Translation.TC_MY_ENTITY_SHORT_NAME),
  width: 150,
  filter: false,
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const ROE: ColDef = {
  ...BASE_ROE,
  cellRendererParams: {
    getModificationType: () => null,
    validationField: MidTradeEventFieldNames.ROE
  },
  editable: false,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal?.rightOfExit
}

const ROS: ColDef = {
  ...BASE_ROS,
  cellRendererParams: {
    getModificationType: () => null,
    validationField: MidTradeEventFieldNames.ROS
  },
  editable: false,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal?.rightOfSubstitution
}

const MY_FICC_ENTITY_SHORT_NAME: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_MY_FICC_ENTITY_SHORT_NAME,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.ficcShortName,
  headerName: Locale.translate(Translation.TC_MY_FICC_ENTITY_SHORT_NAME),
  width: 150,
  filter: false,
  enableRowGroup: false,
  hide: true
}

const FACTOR: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_FACTOR,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.factor,
  headerName: Locale.translate(Translation.TC_FACTOR),
  width: 150,
  filter: false,
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const NEW_FACTOR: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_FACTOR,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) =>
    getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields?.FACTOR?.negotiableFieldRequired),
  headerName: Locale.translate(Translation.TC_NEW_FACTOR),
  width: 150,
  filter: false,
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const ASSET_TYPE: ColDef = {
  ...BASE_ASSET_TYPE,
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_ASSET_TYPE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.assetType,
  width: 150,
  filter: false,
  getQuickFilterText: timeQuickFilterTextGetter,
  enableRowGroup: false,
  hide: true
}

const ISSUE: ColDef = {
  ...BASE_ISSUE,
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_ISSUE,
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params.data?.deal.collateralIssue,
  enableRowGroup: false,
  hide: true
}

const MARKET_TRADE_DATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_MARKET_TRADE_DATE,
  headerName: Locale.translate(Translation.TC_MARKET_TRADE_DATE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.deal?.marketTradeDate,
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeDateFormatter()
}

const MID_TRADE_MARKET_TRADE_DATE: ColDef = {
  colId: MidTradeEventsGridColumnId.MID_TRADE_EVENT_TRADE_DATE,
  headerName: Locale.translate(Translation.TC_MARKET_MID_TRADE_DATE),
  valueGetter: (params: TypedValueGetterParams<DealDetail>) => params?.data?.deal.servicingMarketTradeDate,
  width: 150,
  filter: 'agSetColumnFilter',
  valueFormatter: makeDateFormatter()
}

const VISIBLE_COLUMNS = [
  CHECKBOX,
  GROUPING,
  ACTIONS,
  STATUS,
  EVENT_TYPE,
  ROE,
  ROS,
  COUNTERPARTY_NAME,
  MY_ENTITY,
  EFFECTIVE_DATE,
  MARKET_TRADE_DATE,
  MID_TRADE_MARKET_TRADE_DATE,
  DIRECTION,
  COLLATERAL_ISIN_OR_BUI,
  NEW_ISIN,
  COLLATERAL_NAME,
  NEW_COLLATERAL_NAME,
  SECONDARY_COLLATERAL_NAME,
  NEW_SECONDARY_COLLATERAL_NAME,
  STOCK_BORROWING_LENDING_TYPE,
  NEW_STOCK_BORROWING_LENDING_TYPE,
  NOTIONAL,
  NOTIONAL_GROUP,
  RATE,
  RATE_GROUP,
  FEE,
  NEW_FEE,
  START_CONSIDERATION,
  NEW_START_CONSIDERATION,
  START_CONSIDERATION_OVERRIDE,
  NEW_START_CONSIDERATION_OVERRIDE,
  BOND_PRICE,
  NEW_BOND_PRICE,
  DEAL_ID,
  LAST_UPDATED_BY
]

export const getHiddenColumns: (isAdmin: boolean) => (ColGroupDef | ColDef)[] = (isAdmin: boolean) =>
  [
    COLLATERAL_CURRENCY,
    COLLATERAL_CUSIP,
    NEW_COLLATERAL_CUSIP,
    INVESTMENT_DECISION_MAKER,
    EXECUTION_DECISION_MAKER,
    END_CONSIDERATION,
    NEW_END_CONSIDERATION,
    END_DATE,
    NEW_END_DATE,
    HAIRCUT,
    HAIRCUT_GROUP,
    HAIRCUT_TYPE,
    NEW_HAIRCUT_TYPE,
    REFERENCE_RATE_NAME,
    FLOAT_CRYSTALLISATION_DAYS,
    DEAL_UPDATED_AT_DATE,
    DEAL_UPDATED_AT_TIME,
    MARKET,
    NOTICE_PERIOD_DAYS,
    PRODUCT,
    NEW_REPO_INTEREST,
    SECTOR,
    START_DATE,
    NEW_START_DATE,
    DEAL_TERM,
    NEW_DEAL_TERM,
    TRADE_DATE,
    TRADE_TIME,
    MY_ENTITY_SHORT_NAME,
    ...(isAdmin ? [MY_FICC_ENTITY_SHORT_NAME] : []),
    FACTOR,
    NEW_FACTOR,
    ASSET_TYPE,
    ISSUE,
    SUBMITTING_USER,
    BASE_MARKET_TYPE,
    createUTI(isAdmin)
  ].sort((a, b) => (a.headerName && b.headerName ? a.headerName.localeCompare(b.headerName) : 0))

export const getMidTradeEventsGridColumnDefs: (isAdmin: boolean) => (ColGroupDef | ColDef)[] = isAdmin =>
  VISIBLE_COLUMNS.concat(getHiddenColumns(isAdmin))

export const midTradeEventsGridOptions: GridOptions<DealDetail> = {
  ...gridOptionsBase,
  getRowId: ({ data }) => (!data.isDealHistory ? data.deal.dealId : `${data.deal.dealId}-${data.deal.version}`)
}
